import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, useTheme, Button, useMediaQuery, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import config from "../../config";
import { AuthContext } from "../../components/AuthContext";
import { getAuthToken } from "../../utils/authStorage";
import MemberStatusChip, { FreeCardChip, MEMBER_STATUS_OPTIONS } from "../../components/MemberStatusChip";

const MemberList = () => {
  const theme = useTheme();
  const isTabletOrBelow = useMediaQuery(theme.breakpoints.down("lg"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const colors = tokens(theme.palette.mode);
  const [selectedRow, setSelectedRow] = useState(null);
  const navigate = useNavigate();
  const [memberList, setMemberList] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const { role } = useContext(AuthContext);
  const isAdmin = role === "global_admin" || role === "branch_admin";

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const token = getAuthToken();
        const response = await axios.get(
          `${config.apiBaseUrl}/get_members`,
          {
            params: statusFilter === "free_card"
              ? { free_card: 1 }
              : statusFilter !== "all" ? { status: statusFilter } : {},
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );
        console.log(response.data);
        setMemberList(response.data["members"]);
        console.log(memberList);
      } catch (error) {
        console.error("Failed to fetch members:", error);
        toast.error("Failed to fetch members");
      }
    };

    fetchMembers();
  }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = [
    // { field: "member_id", headerName: "ID" },
    {
      field: "membership_id",
      headerName: "Membership ID",
      flex: 1,
      minWidth: 170,
      cellClassName: "name-column--cell",
    },
    {
      field: "first_name",
      headerName: "First Name",
      flex: 1,
      minWidth: 140,
      cellClassName: "name-column--cell",
    },
    {
      field: "last_name",
      headerName: "Last Name",
      flex: 1,
      minWidth: 140,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 200,
      renderCell: ({ value, row }) => (
        <Box display="flex" gap={0.75} alignItems="center">
          <MemberStatusChip status={value} />
          {isAdmin && Number(row.is_free_card) === 1 && <FreeCardChip />}
        </Box>
      ),
    },
    {
      field: "nic",
      headerName: "NIC",
      flex: 1,
    },
    {
      field: "discipline_name_one",
      headerName: "Primary Discipline",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "subscription_name",
      headerName: "Subscription",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "rfid",
      headerName: "RFID",
      flex: 1,
    },
    {
      field: "home_address",
      headerName: "Home Address",
      flex: 1,
    },
    {
      field: "city",
      headerName: "City",
      flex: 1,
    },
    {
      field: "country",
      headerName: "Country",
      flex: 1,
    },
    {
      field: "branch_name",
      headerName: "Branch",
      flex: 1,
      minWidth: 130,
    },
    {
      field: "student_dob",
      headerName: "Student DOB",
      flex: 1,
    },
    {
      field: "occupation",
      headerName: "Occupation",
      flex: 1,
    },
    {
      field: "gender",
      headerName: "Gender",
      flex: 1,
    },
    {
      field: "residence_phone",
      headerName: "Residence Phone",
      flex: 1,
    },
    {
      field: "mobile_phone",
      headerName: "Mobile Phone",
      flex: 1,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
    },
    {
      field: "whatsapp_number",
      headerName: "WhatsApp Number",
      flex: 1,
    },
    {
      field: "emergency_contact_person_name",
      headerName: "Emergency Contact Person Name",
      flex: 1,
    },
    {
      field: "emergency_contact_number",
      headerName: "Emergency Contact Number",
      flex: 1,
    },
    {
      field: "family_id",
      headerName: "Family",
      flex: 1,
      minWidth: 110,
    },
  ];

  const handleSelectionChange = (newSelection) => {
    setSelectedRow(newSelection.length === 1 ? newSelection[0] : null);
    console.log(selectedRow);
  };

  const handleUpdate = () => {
    if (selectedRow) {
      console.log("Selected Row ", selectedRow);
      const selectMember = memberList.find((member) => member.member_id === selectedRow);
      console.log(selectMember);
      navigate("/manage_member", { state: { member: selectMember } });
    }
  }

  const handleAddFamilyMember = () => {
    if (selectedRow) {
      console.log("Selected Row ", selectedRow);
      const selectMember = memberList.find((member) => member.member_id === selectedRow);
      if (selectMember) {
        console.log("Family ID: ", selectMember.family_id)
        console.log("Subscription ID: ", selectMember.subscription_id)
        navigate("/manage_member", { state: { member: { family_id: selectMember.family_id, subscription_id: selectMember.subscription_id } } });
      }
    }
  }

  const handleViewProfile = () => {
    if (selectedRow) {
      console.log("Selected Row ", selectedRow);
      const selectMember = memberList.find((member) => member.member_id === selectedRow);
      console.log(selectMember);
      navigate("/view_member", { state: { member: selectMember } });
    }
  };

  const handlePayment = () => {
    if (selectedRow) {
      console.log("Selected Row ", selectedRow);
      const selectMember = memberList.find((member) => member.member_id === selectedRow);
      console.log(selectMember);
      navigate("/teba/payment-receive", { state: { membershipId: selectMember?.membership_id } });
    }
  };

  return (
    <Box m={{ xs: "12px", sm: "16px", md: "20px" }}>
      <Header title="Members" subtitle="Member List" />
      <FormControl size="small" sx={{ mt: 2, minWidth: 200 }}>
        <InputLabel id="member-status-filter-label">Status</InputLabel>
        <Select
          labelId="member-status-filter-label"
          value={statusFilter}
          label="Status"
          onChange={(event) => setStatusFilter(event.target.value)}
        >
          <MenuItem value="all">All statuses</MenuItem>
          {MEMBER_STATUS_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))}
          {isAdmin && <MenuItem value="free_card">Free card</MenuItem>}
        </Select>
      </FormControl>
      {selectedRow && (
        <Box display="flex" justifyContent="flex-start" flexWrap="wrap" gap={1} mt={2}>
          {isAdmin && (
            <>
              <Button variant="contained" color="success" sx={{ mr: { xs: 0, sm: "10px" } }} onClick={handleUpdate} size={isMobile ? "small" : "medium"}>
                Update
              </Button>
              <Button variant="contained" color="error" sx={{ mr: { xs: 0, sm: "10px" } }} size={isMobile ? "small" : "medium"}>
                Delete
              </Button>
              <Button variant="contained" color="secondary" sx={{ mr: { xs: 0, sm: "10px" } }} onClick={handleAddFamilyMember} size={isMobile ? "small" : "medium"}>
                Add Family Member
              </Button>
            </>
          )}
          <Button
            variant="contained"
            sx={{ backgroundColor: 'orange', color: 'black', mr: { xs: 0, sm: "10px" } }}
            onClick={handleViewProfile}
            size={isMobile ? "small" : "medium"}
          >
            View Profile
          </Button>
          {isAdmin && (
            <Button
              variant="contained"
              sx={{ backgroundColor: '#2ecc71', color: 'white', mr: { xs: 0, sm: "10px" } }}
              onClick={handlePayment}
              size={isMobile ? "small" : "medium"}
            >
              Pay
            </Button>
          )}
        </Box>
      )}
      <Box
        mt={{ xs: 2, sm: 2.5, md: 4 }}
        height={{ xs: "62vh", sm: "68vh", md: "75vh" }}
        minHeight={{ xs: 360, sm: 420, md: 480 }}
        sx={{
          overflowX: "auto",
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
            display: "flex",
            alignItems: "center",
            fontSize: { xs: "0.78rem", sm: "0.84rem", md: "0.875rem" },
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontSize: { xs: "0.74rem", sm: "0.8rem", md: "0.875rem" },
            fontWeight: 700,
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
        }}
      >
        <Box
          sx={{
            minWidth: isMobile ? 1680 : isTabletOrBelow ? 1400 : "100%",
            height: "100%",
          }}
        >
          <DataGrid
            checkboxSelection
            rows={Array.isArray(memberList) ? memberList : []}
            columns={columns}
            getRowId={(row) => row.member_id}
            onRowSelectionModelChange={handleSelectionChange}
            rowHeight={52}
            columnBuffer={4}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  nic: false,
                  home_address: false,
                  city: false,
                  country: false,
                  student_dob: false,
                  occupation: false,
                  gender: false,
                  residence_phone: false,
                  mobile_phone: false,
                  email: false,
                  whatsapp_number: false,
                  emergency_contact_person_name: false,
                  emergency_contact_number: false,
                }
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default MemberList;